import { EDetailPageTopBarItemType } from '@app/detailPageTopBar.model';
import { AxiosErrorResponse } from '@core/error.models';
import { ComponentType, IconName } from '@gcl/react-core';
import { UseMutateFunction, UseMutationResult } from '@tanstack/react-query';
import * as React from 'react';
import { useMutationWithOnSuccessOnError } from './useMutationWithOnSuccessOnError.hooks';

interface IUseMutationWithPageTopBarLoadingButtonProps<TReturn> {
  title: string;
  componentType: ComponentType;
  onClick: (mutate: UseMutateFunction<TReturn, AxiosErrorResponse, any, unknown>) => void;
  disable: boolean;
  useMutation: () => UseMutationResult<TReturn, AxiosErrorResponse, any, unknown>;
  onSuccess: (variables: any, result: TReturn) => void;
  onError?: (variables: any, error: any) => void;
  shouldResetOnError?: boolean;
  buttonIconName?: IconName;
}

export const useMutationWithPageTopBarLoadingButton = <TReturn>({
  title,
  componentType,
  onClick,
  disable,
  useMutation,
  onSuccess,
  onError,
  shouldResetOnError = false,
  buttonIconName = undefined,
}: IUseMutationWithPageTopBarLoadingButtonProps<TReturn>) => {
  const mutationResult = useMutationWithOnSuccessOnError(useMutation, onSuccess, onError, shouldResetOnError);
  const { data, mutate, isSuccess, isError, isLoading } = mutationResult;

  const loadingButtonProps = React.useMemo(() => {
    return {
      key: title,
      title,
      type: EDetailPageTopBarItemType.loadingButton,
      componentType,
      isError,
      isLoading,
      isSuccess,
      onClick: () => {
        onClick(mutate);
      },
      disable,
      buttonIconName,
    };
  }, [isError, isLoading, isSuccess, title, componentType, JSON.stringify(mutate), onClick, disable]);

  return { ...mutationResult, loadingButtonProps };
};
